/**
 * AdminProdReadiness — production-readiness audit dashboard.
 *
 * Why it exists:
 *   2026-05-22 — a paid customer was lost when test-mode Stripe keys
 *   leaked into production. This dashboard is the operator-facing
 *   half of the structural fix (boot gate + email + CLI cover the
 *   automated half). One-click readiness before every deploy.
 *
 * Layout:
 *   - Headline status pill (PASS / WARN / FAIL) — visible from across
 *     the room. Color-coded.
 *   - Per-check rows grouped by severity (critical first), with the
 *     observed value (last-4 masked) so operators can spot
 *     "sk_test_…" instantly.
 *   - Manual refresh + last-checked timestamp.
 *
 * Auth: admin-only. Non-admins redirect to /dashboard.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, ShieldAlert, RefreshCw, AlertTriangle, AlertCircle, Info,
  Check, X, Loader2, Lock, Settings,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminSubNav from '../components/AdminSubNav';

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };
const SEVERITY_COLOR = {
  critical: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', accent: '#dc2626', label: 'Critical' },
  warning:  { bg: '#fffbeb', border: '#fde68a', text: '#92400e', accent: '#f59e0b', label: 'Warning' },
  info:     { bg: '#f0f9ff', border: '#bae6fd', text: '#075985', accent: '#0284c7', label: 'Info' },
};

const StatusPill = ({ status }) => {
  if (status === 'pass') {
    return (
      <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#ecfdf5] border border-[#a7f3d0] text-[#065f46] font-bold text-sm" data-testid="status-pill">
        <ShieldCheck size={16} /> Production-ready
      </span>
    );
  }
  if (status === 'warn') {
    return (
      <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#fffbeb] border border-[#fde68a] text-[#92400e] font-bold text-sm" data-testid="status-pill">
        <AlertTriangle size={16} /> Ready with warnings
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#fef2f2] border border-[#fecaca] text-[#991b1b] font-bold text-sm" data-testid="status-pill">
      <ShieldAlert size={16} /> NOT production-ready
    </span>
  );
};

const SeverityIcon = ({ severity }) => {
  if (severity === 'critical') return <AlertCircle size={16} className="text-[#dc2626]" />;
  if (severity === 'warning') return <AlertTriangle size={16} className="text-[#f59e0b]" />;
  return <Info size={16} className="text-[#0284c7]" />;
};

const CheckRow = ({ check }) => {
  const color = SEVERITY_COLOR[check.severity] || SEVERITY_COLOR.info;
  return (
    <div
      className={`p-4 border ${check.passed ? 'border-[#e5e7eb] bg-white' : 'border-l-4'}`}
      style={!check.passed ? { borderColor: color.border, borderLeftColor: color.accent, backgroundColor: color.bg } : undefined}
      data-testid={`check-${check.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {check.passed
            ? <Check size={18} className="text-[#10b981]" />
            : <X size={18} style={{ color: color.accent }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#0f1115] text-sm">{check.name}</span>
            {!check.passed && (
              <span
                className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                style={{ color: color.accent, backgroundColor: 'white', border: `1px solid ${color.border}` }}
              >
                {color.label}
              </span>
            )}
            <SeverityIcon severity={check.severity} />
          </div>
          {!check.passed && (
            <p className="text-xs mt-1.5" style={{ color: color.text }} data-testid={`check-${check.id}-message`}>
              {check.message}
            </p>
          )}
          {check.observed_value && (
            <p className="text-[11px] mt-1 font-mono text-[#6b7280] truncate" data-testid={`check-${check.id}-observed`}>
              <Lock size={9} className="inline mr-1" />
              observed: <span className="text-[#0f1115]">{check.observed_value}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminProdReadiness = () => {
  const { user, loading: authLoading } = useAuth();
  useHideCookieBanner();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Env-verify panel — separate from main checks. Used after the
  // operator sets STRICT_PROD_MODE / ALERT_EMAIL in the Emergent
  // platform UI to confirm the values were picked up by the running
  // backend (vs. only existing in the platform config).
  const [envData, setEnvData] = useState(null);
  const [envLoading, setEnvLoading] = useState(false);
  const [envErr, setEnvErr] = useState('');

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/admin/prod-readiness`, { withCredentials: true });
      setData(res.data);
      setLastUpdated(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load readiness report.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  // Env-verify panel handler — pings the backend to confirm STRICT_PROD_MODE
  // and ALERT_EMAIL have actually been picked up by the running process.
  // Was referenced from the panel button but never defined — caused a
  // ReferenceError → "Something went sideways" crash on every /admin/prod-readiness
  // load. Fixed 2026-05-23.
  const verifyEnv = useCallback(async () => {
    setEnvLoading(true);
    setEnvErr('');
    try {
      const res = await axios.get(`${API}/admin/prod-readiness/env-verify`, { withCredentials: true });
      setEnvData(res.data);
    } catch (e) {
      setEnvErr(e?.response?.data?.detail || 'Failed to verify production env.');
    } finally {
      setEnvLoading(false);
    }
  }, []);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const sortedChecks = data?.checks
    ? [...data.checks].sort((a, b) => {
        // Failing checks first, then by severity order
        if (a.passed !== b.passed) return a.passed ? 1 : -1;
        return (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9);
      })
    : [];

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-prod-readiness-page">
      <AdminSubNav />
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN · DEPLOY GUARDRAIL</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115]">Production Readiness</h1>
            <p className="text-sm text-[#6b7280]">
              Hit "Refresh" before every deploy. Lost a paying customer to a test-key leak on 2026-05-22 — this is the structural fix.
            </p>
          </div>
          <button
            onClick={() => load(false)}
            disabled={refreshing}
            className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="manual-refresh"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Auditing…' : 'Refresh'}
          </button>
        </div>

        {err && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 flex items-center gap-2" data-testid="readiness-error">
            <AlertTriangle size={16} /> {err}
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-[#6b7280]">
            <Loader2 className="animate-spin mr-2" size={20} /> Running checks…
          </div>
        ) : data ? (
          <>
            {/* Headline status */}
            <div className="bg-white border border-[#e5e7eb] p-5 mb-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <StatusPill status={data.status} />
                <div className="text-right">
                  <div className="font-satoshi text-2xl font-black text-[#0f1115]" data-testid="passed-count">
                    {data.passed}/{data.total_checks}
                  </div>
                  <div className="text-xs text-[#6b7280]">checks passing</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="p-2 bg-[#fef2f2] border border-[#fecaca]" data-testid="summary-critical">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#991b1b]">Critical</div>
                  <div className="font-satoshi text-xl font-black text-[#dc2626]">{data.critical_failures}</div>
                </div>
                <div className="p-2 bg-[#fffbeb] border border-[#fde68a]" data-testid="summary-warning">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#92400e]">Warnings</div>
                  <div className="font-satoshi text-xl font-black text-[#f59e0b]">{data.warning_failures}</div>
                </div>
                <div className={`p-2 border ${data.strict_mode ? 'bg-[#ecfdf5] border-[#a7f3d0]' : 'bg-[#f9fafb] border-[#e5e7eb]'}`} data-testid="summary-strict">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#0f1115]">Strict mode</div>
                  <div className="font-satoshi text-xl font-black text-[#0f1115]">{data.strict_mode ? 'ON' : 'off'}</div>
                </div>
              </div>
              {data.checked_at && (
                <p className="text-xs text-[#6b7280] mt-3" data-testid="checked-at">
                  Audited {new Date(data.checked_at).toLocaleString()} · Refresh to re-run.
                </p>
              )}
            </div>

            {/* Env-verify panel — click-to-confirm production env vars
                actually loaded into the running backend. Operator runs
                this AFTER setting STRICT_PROD_MODE / ALERT_EMAIL in the
                Emergent platform UI. */}
            <div className="bg-white border border-[#e5e7eb] p-5 mb-6" data-testid="env-verify-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                  <Settings size={16} className="text-[#002fa7]" />
                  <h2 className="font-satoshi text-base font-bold text-[#0f1115]">Production env verification</h2>
                </div>
                <button
                  onClick={verifyEnv}
                  disabled={envLoading}
                  className="px-3 py-1.5 text-xs font-semibold bg-[#0f1115] text-white hover:bg-[#002fa7] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="verify-env-button"
                >
                  {envLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {envLoading ? 'Checking…' : 'Verify production env'}
                </button>
              </div>
              <p className="text-xs text-[#6b7280] mb-3">
                Click after setting <code className="bg-[#f9fafb] px-1">STRICT_PROD_MODE</code> and{' '}
                <code className="bg-[#f9fafb] px-1">ALERT_EMAIL</code> in your Emergent deployment settings.
                Confirms the running backend has picked them up.
              </p>
              {envErr && (
                <div className="text-xs text-red-700 mb-2" data-testid="env-verify-error">{envErr}</div>
              )}
              {envData && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="env-verify-result">
                  <div className={`p-3 border ${envData.strict_prod_mode.enabled ? 'border-[#a7f3d0] bg-[#ecfdf5]' : 'border-[#fde68a] bg-[#fffbeb]'}`} data-testid="env-strict">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#6b7280]">STRICT_PROD_MODE</div>
                    <div className="font-satoshi text-lg font-black text-[#0f1115] mt-0.5">
                      {envData.strict_prod_mode.enabled ? 'ON' : 'off'}
                    </div>
                    <div className="text-[11px] text-[#6b7280] mt-0.5 font-mono">
                      value: {envData.strict_prod_mode.value || '<unset>'}
                    </div>
                  </div>
                  <div className={`p-3 border ${envData.alert_email.set ? 'border-[#a7f3d0] bg-[#ecfdf5]' : 'border-[#fde68a] bg-[#fffbeb]'}`} data-testid="env-alert-email">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#6b7280]">ALERT_EMAIL</div>
                    <div className="font-satoshi text-lg font-black text-[#0f1115] mt-0.5">
                      {envData.alert_email.set ? 'set' : 'not set'}
                    </div>
                    <div className="text-[11px] text-[#6b7280] mt-0.5 font-mono">
                      {envData.alert_email.value || '<unset>'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Per-check rows */}
            <div className="space-y-2" data-testid="checks-list">
              {sortedChecks.map((c) => (
                <CheckRow key={c.id} check={c} />
              ))}
            </div>

            {data.status === 'fail' && (
              <div className="mt-6 p-4 bg-[#0f1115] text-white" data-testid="fail-call-to-action">
                <p className="text-sm font-bold">DO NOT DEPLOY.</p>
                <p className="text-xs mt-1 text-white/70">
                  Fix every critical issue above. Then hit Refresh. Then deploy.
                </p>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AdminProdReadiness;
